import { useState } from 'react';
import { ActionError, ReadOnlyNote, useAction } from '../../components/action.tsx';
import { DataTable } from '../../components/data-table.tsx';
import { formatDate } from '../../components/format.ts';
import { Loaded } from '../../components/page.tsx';
import { useConsole, useHasRole, useReloadableTenantQuery } from '../../data/console-context.tsx';
import type { ConsoleData, TenantSummary } from '../../data/console-data.ts';
import { isConnectedByEachUser } from '../../data/rows.ts';

const loadAccounts = async (data: ConsoleData, tenant: TenantSummary) => {
  const [accounts, setup] = await Promise.all([
    data.listTestAccounts(tenant.id),
    data.getIntegrationSetup(tenant.id),
  ]);
  const servers = setup.mcpServers.filter((server) => !isConnectedByEachUser(server));
  return { accounts, servers: servers.map((server) => server.name) };
};

export function TestAccountsPanel() {
  const { query, reload } = useReloadableTenantQuery(loadAccounts);
  const canEdit = useHasRole('admin');
  const { data, tenant } = useConsole();
  const action = useAction();
  return (
    <section>
      <p className="muted">
        A test account is a user of your product the assistant acts as when it runs a case or when
        your team tries it. Use one whose data you don't mind it reading and changing.
      </p>
      {!canEdit && <ReadOnlyNote needs="admin" />}
      <Loaded query={query}>
        {({ accounts, servers }) => (
          <>
            {canEdit && <RegisterForm servers={servers} onRegistered={reload} />}
            <DataTable
              caption="Test accounts"
              columns={[
                { header: 'Name', cell: (row) => row.name },
                {
                  header: 'Reaches',
                  cell: (row) => (row.mcp_servers.length === 0 ? '—' : row.mcp_servers.join(', ')),
                },
                { header: 'Updated', cell: (row) => formatDate(row.updated_at) },
                ...(canEdit
                  ? [
                      {
                        header: 'Actions',
                        cell: (row: (typeof accounts)[number]) => (
                          <button
                            type="button"
                            className="danger"
                            disabled={action.pending}
                            onClick={() =>
                              void action
                                .run(() => data.removeTestAccount(tenant.id, row.name))
                                .then((ok) => ok && reload())
                            }
                          >
                            Remove
                          </button>
                        ),
                      },
                    ]
                  : []),
              ]}
              rows={accounts}
            />
            {accounts.length === 0 && <p className="muted">No test accounts yet.</p>}
            <ActionError error={action.error} />
          </>
        )}
      </Loaded>
    </section>
  );
}

function RegisterForm(props: { servers: readonly string[]; onRegistered: () => void }) {
  const { data, tenant } = useConsole();
  const [name, setName] = useState('');
  const [server, setServer] = useState(props.servers[0] ?? '');
  const [accessToken, setAccessToken] = useState('');
  const [refreshToken, setRefreshToken] = useState('');
  const action = useAction();
  return (
    <form
      className="form"
      onSubmit={(event) => {
        event.preventDefault();
        void action.run(async () => {
          await data.registerTestAccount(tenant.id, {
            name,
            servers:
              server === ''
                ? []
                : [
                    {
                      name: server,
                      access_token: accessToken,
                      ...(refreshToken !== '' ? { refresh_token: refreshToken } : {}),
                    },
                  ],
          });
          setName('');
          setAccessToken('');
          setRefreshToken('');
          props.onRegistered();
        });
      }}
    >
      <h2>Register a test account</h2>
      <fieldset disabled={action.pending}>
        <label>
          Name
          <span className="hint">
            Lower-case letters, digits and hyphens. Using a name again replaces it.
          </span>
          <input
            value={name}
            required
            maxLength={40}
            pattern="[a-z0-9](?:[a-z0-9-]{0,38}[a-z0-9])?"
            onChange={(e) => setName(e.target.value)}
          />
        </label>
        <label>
          Tool source
          <select value={server} onChange={(e) => setServer(e.target.value)}>
            {props.servers.map((entry) => (
              <option key={entry} value={entry}>
                {entry}
              </option>
            ))}
            <option value="">None: no access to your product</option>
          </select>
        </label>
        {server !== '' && (
          <>
            <label>
              Access token
              <span className="hint">The test user's token, as your backend would mint it.</span>
              <input
                type="password"
                autoComplete="off"
                required
                value={accessToken}
                onChange={(e) => setAccessToken(e.target.value)}
              />
            </label>
            <label>
              Refresh token
              <span className="hint">
                Optional. Without one the account stops working when the access token expires.
              </span>
              <input
                type="password"
                autoComplete="off"
                value={refreshToken}
                onChange={(e) => setRefreshToken(e.target.value)}
              />
            </label>
          </>
        )}
        <div className="row">
          <button type="submit" className="primary">
            Register
          </button>
        </div>
      </fieldset>
      <ActionError error={action.error} />
    </form>
  );
}
